import React, { useState, useMemo } from 'react'
import { Table, Code, Copy, Check, ChevronRight, ChevronDown } from 'lucide-react'
import styles from './DataViewer.module.scss'

type ViewMode = 'json' | 'table'

interface DataViewerProps {
  data: unknown
  title?: string
  defaultView?: ViewMode
  maxHeight?: number
}

interface TableRowProps {
  keyName: string
  value: unknown
  depth: number
  path: string
}

const isObject = (val: unknown): val is Record<string, unknown> => {
  return typeof val === 'object' && val !== null && !Array.isArray(val)
}

const isArray = (val: unknown): val is unknown[] => {
  return Array.isArray(val)
}

const isPrimitive = (val: unknown): boolean => {
  return val === null || val === undefined || ['string', 'number', 'boolean'].includes(typeof val)
}

const formatValue = (val: unknown): string => {
  if (val === null) return 'null'
  if (val === undefined) return 'undefined'
  if (typeof val === 'string') return val
  if (typeof val === 'number') return val.toLocaleString()
  if (typeof val === 'boolean') return val ? 'true' : 'false'
  return String(val)
}

const getValueType = (val: unknown): string => {
  if (val === null) return 'null'
  if (val === undefined) return 'undefined'
  if (isArray(val)) return 'array'
  if (isObject(val)) return 'object'
  return typeof val
}

const TableRow: React.FC<TableRowProps> = ({ keyName, value, depth, path }) => {
  const [expanded, setExpanded] = useState(depth < 2)
  const hasChildren = isObject(value) || isArray(value)
  const childCount = hasChildren
    ? isArray(value)
      ? value.length
      : Object.keys(value as Record<string, unknown>).length
    : 0
  const valueType = getValueType(value)

  const toggleExpand = () => {
    if (hasChildren) {
      setExpanded(!expanded)
    }
  }

  return (
    <>
      <tr className={styles.tableRow}>
        <td className={styles.keyCell} style={{ paddingLeft: `${depth * 16 + 12}px` }}>
          {hasChildren ? (
            <button className={styles.expandBtn} onClick={toggleExpand}>
              {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
            </button>
          ) : (
            <span className={styles.expandPlaceholder} />
          )}
          <span className={styles.keyName}>{keyName}</span>
        </td>
        <td className={styles.typeCell}>
          <span className={`${styles.typeBadge} ${styles[valueType]}`}>
            {valueType}
            {hasChildren && <span className={styles.childCount}>[{childCount}]</span>}
          </span>
        </td>
        <td className={styles.valueCell}>
          {isPrimitive(value) ? (
            <span className={`${styles.primitiveValue} ${styles[valueType]}`}>{formatValue(value)}</span>
          ) : hasChildren && !expanded ? (
            <span className={styles.collapsedValue}>
              {isArray(value)
                ? `[${value.length} items]`
                : `{${Object.keys(value as Record<string, unknown>).length} keys}`}
            </span>
          ) : null}
        </td>
      </tr>
      {hasChildren && expanded && (
        <>
          {isArray(value)
            ? value.map((item, index) => (
                <TableRow
                  key={`${path}.${index}`}
                  keyName={String(index)}
                  value={item}
                  depth={depth + 1}
                  path={`${path}.${index}`}
                />
              ))
            : Object.entries(value as Record<string, unknown>).map(([k, v]) => (
                <TableRow key={`${path}.${k}`} keyName={k} value={v} depth={depth + 1} path={`${path}.${k}`} />
              ))}
        </>
      )}
    </>
  )
}

const DataViewer: React.FC<DataViewerProps> = ({ data, title, defaultView = 'table', maxHeight = 400 }) => {
  const [viewMode, setViewMode] = useState<ViewMode>(defaultView)
  const [copied, setCopied] = useState(false)

  const jsonString = useMemo(() => {
    try {
      return JSON.stringify(data, null, 2)
    } catch {
      return 'Unable to serialize data'
    }
  }, [data])

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(jsonString)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  const renderTableView = () => {
    if (data === null || data === undefined) {
      return (
        <div className={styles.emptyData}>
          <span>No data</span>
        </div>
      )
    }

    if (isPrimitive(data)) {
      return (
        <div className={styles.primitiveWrapper}>
          <span className={`${styles.primitiveValue} ${styles[getValueType(data)]}`}>{formatValue(data)}</span>
        </div>
      )
    }

    const entries = isArray(data)
      ? data.map((item, index) => [String(index), item] as [string, unknown])
      : Object.entries(data as Record<string, unknown>)

    return (
      <div className={styles.tableWrapper} style={{ maxHeight }}>
        <table className={styles.dataTable}>
          <thead>
            <tr>
              <th className={styles.keyHeader}>Key</th>
              <th className={styles.typeHeader}>Type</th>
              <th className={styles.valueHeader}>Value</th>
            </tr>
          </thead>
          <tbody>
            {entries.map(([key, value]) => (
              <TableRow key={key} keyName={key} value={value} depth={0} path={key} />
            ))}
          </tbody>
        </table>
      </div>
    )
  }

  const renderJsonView = () => {
    return (
      <div className={styles.jsonWrapper} style={{ maxHeight }}>
        <pre className={styles.jsonContent}>{jsonString}</pre>
      </div>
    )
  }

  return (
    <div className={styles.dataViewer}>
      <div className={styles.header}>
        {title && <span className={styles.title}>{title}</span>}
        <div className={styles.controls}>
          <div className={styles.viewToggle}>
            <button
              className={`${styles.toggleBtn} ${viewMode === 'table' ? styles.active : ''}`}
              onClick={() => setViewMode('table')}
              title="Table View">
              <Table size={14} />
              <span>Table</span>
            </button>
            <button
              className={`${styles.toggleBtn} ${viewMode === 'json' ? styles.active : ''}`}
              onClick={() => setViewMode('json')}
              title="JSON View">
              <Code size={14} />
              <span>JSON</span>
            </button>
          </div>
          <button className={styles.copyBtn} onClick={handleCopy} title="Copy JSON">
            {copied ? <Check size={14} /> : <Copy size={14} />}
            <span>{copied ? 'Copied!' : 'Copy'}</span>
          </button>
        </div>
      </div>
      <div className={styles.content}>{viewMode === 'table' ? renderTableView() : renderJsonView()}</div>
    </div>
  )
}

export default DataViewer
